import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise;

const loadScript = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        script.remove();
        scriptPromise = undefined;
        reject(new Error("Unable to load the Turnstile script"));
      };
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
};

/**
 * Cloudflare Turnstile CAPTCHA widget. A token is single-use, so remount this
 * component (change its `key`) to get a fresh challenge after each submission.
 */
const Turnstile = ({ siteKey, onVerify, onExpire, onError }) => {
  const containerRef = useRef(null);
  const callbacks = useRef({});
  callbacks.current = { onVerify, onExpire, onError };

  useEffect(() => {
    if (!siteKey) {
      console.error("REACT_APP_TURNSTILE_SITE_KEY is not set");
      return undefined;
    }

    let widgetId;
    let cancelled = false;

    loadScript()
      .then((turnstile) => {
        if (cancelled) {
          return;
        }

        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => callbacks.current.onVerify(token),
          "expired-callback": () => callbacks.current.onExpire(),
          "error-callback": () => callbacks.current.onError(),
        });
      })
      .catch(() => callbacks.current.onError());

    return () => {
      cancelled = true;

      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} />;
};

Turnstile.propTypes = {
  /** Public Turnstile site key */
  siteKey: PropTypes.string,

  /** Called with the token once the visitor passes the challenge */
  onVerify: PropTypes.func.isRequired,

  /** Called when a previously issued token expires */
  onExpire: PropTypes.func.isRequired,

  /** Called when the widget fails to load or errors out */
  onError: PropTypes.func.isRequired,
};

export default Turnstile;
